// Reglas del alcance del asistente (dónde se aplica la configuración), fuera
// de la vista para poder testearlas sin montar React.
//
// Modelo mental: cada número/página SIGUE a su canal salvo que el usuario lo
// deje distinto. Por eso al guardar solo viajan las diferencias — así una
// excepción no queda congelada cuando el canal cambia de idea después.

/**
 * Estado inicial editable a partir de lo que devuelve el backend.
 * @param {Array} channels
 * @returns {{channelStates: object, accountStates: object}}
 */
export function initialScopeStates(channels) {
  const list = Array.isArray(channels) ? channels : [];
  return {
    channelStates: Object.fromEntries(list.map((c) => [c.channel, c.enabled !== false])),
    accountStates: Object.fromEntries(
      list.flatMap((c) => (c.accounts || []).map((a) => [a.key, a.enabled !== false])),
    ),
  };
}

/**
 * Excepciones a persistir: SOLO las cuentas cuyo estado difiere del de su
 * canal. Las claves salen tal cual del backend (nunca se arman acá).
 * @param {Array} scope
 * @param {object} channelStates
 * @param {object} accountStates
 * @returns {Array<{key: string, enabled: boolean}>}
 */
export function buildAccountOverrides(scope, channelStates, accountStates) {
  return (Array.isArray(scope) ? scope : []).flatMap((c) => {
    const channelOn = channelStates[c.channel] !== false;
    return (c.accounts || [])
      .filter((a) => (accountStates[a.key] !== false) !== channelOn)
      .map((a) => ({ key: a.key, enabled: accountStates[a.key] !== false }));
  });
}

/**
 * Dónde NO responde, en palabras. Un canal apagado se nombra a sí mismo —
 * pero SIEMPRE con sus excepciones a la vista: decir "no responde en WhatsApp"
 * cuando un número de WhatsApp sí responde es peor que no decir nada. Con el
 * canal encendido se nombran sus cuentas apagadas.
 * @param {Array} scope
 * @param {object} channelStates
 * @param {object} accountStates
 * @returns {string[]}
 */
export function disabledScopeLabels(scope, channelStates, accountStates) {
  return (Array.isArray(scope) ? scope : []).flatMap((c) => {
    const accounts = c.accounts || [];
    if (channelStates[c.channel] === false) {
      const on = accounts
        .filter((a) => accountStates[a.key] !== false)
        .map((a) => a.label);
      return [on.length ? `${c.label} (salvo ${on.join(", ")})` : c.label];
    }
    return accounts.filter((a) => accountStates[a.key] === false).map((a) => a.label);
  });
}

/**
 * Apagar/encender un canal arrastra a las cuentas que iban EN LÍNEA con él y
 * respeta las excepciones ya definidas.
 * @param {Array} accounts
 * @param {object} accountStates
 * @param {boolean} from estado anterior del canal
 * @param {boolean} to estado nuevo del canal
 * @returns {object} nuevo accountStates
 */
export function followChannel(accounts, accountStates, from, to) {
  const out = { ...accountStates };
  (accounts || []).forEach((a) => {
    if ((out[a.key] !== false) === from) out[a.key] = to;
  });
  return out;
}

/**
 * Presentación del MSISDN que entrega el backend (solo dígitos). Chile queda
 * legible; cualquier otro largo se muestra con '+' y sin inventar grupos.
 * @param {string} raw
 * @returns {string}
 */
export function formatScopePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  const m = digits.match(/^56(9)(\d{4})(\d{4})$/);
  return m ? `+56 ${m[1]} ${m[2]} ${m[3]}` : `+${digits}`;
}
